import {
  useAssignSettingShowExpectedAddToCartAmountOnAtc,
  useSettingShowExpectedAddToCartAmountOnAtc,
} from "@faire/deprecated-retailer-visitor-shared/settings/useSettingShowExpectedAddToCartAmountOnAtc";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { useFormatMoney } from "@faire/web/common/money";
import { useEffect, useMemo } from "react";

import { AddToCartCtaState } from "./AddToCartButton";

const SHOW_PRICE_STATES: AddToCartCtaState[] = [
  "UPDATE_DIFFERENT_PRODUCT",
  "UPDATE_DEFAULT",
  "PREORDER_DEFAULT",
  "DEFAULT",
];

export const useAddToCartText = (
  addToCartCtaState: AddToCartCtaState,
  isUpdating: boolean,
  productAddCTAOverride?:
    | ((isUpdating: boolean, strictLocalize: StrictLocalizeFunction) => string)
    | undefined,
  expectedAddToCartAmount?: IFaireMoney | undefined
) => {
  const { strictLocalize } = useStrictLocalization();
  const { formatMoney } = useFormatMoney();
  const isShowExpectedAddToCartAmountOnAtc =
    useSettingShowExpectedAddToCartAmountOnAtc();
  const assignShowExpectedAddToCartAmountOnAtc =
    useAssignSettingShowExpectedAddToCartAmountOnAtc();
  const shouldShowExpectedAddToCartAmount = useMemo(() => {
    return (
      !!expectedAddToCartAmount?.amount_cents &&
      SHOW_PRICE_STATES.includes(addToCartCtaState)
    );
  }, [addToCartCtaState, expectedAddToCartAmount?.amount_cents]);

  useEffect(() => {
    if (shouldShowExpectedAddToCartAmount) {
      assignShowExpectedAddToCartAmountOnAtc();
    }
  }, [
    assignShowExpectedAddToCartAmountOnAtc,
    shouldShowExpectedAddToCartAmount,
  ]);

  if (productAddCTAOverride && addToCartCtaState !== "UNAVAILABLE") {
    return productAddCTAOverride(isUpdating, strictLocalize);
  }

  const addToCartText = getAddToCartText(addToCartCtaState, strictLocalize);

  if (shouldShowExpectedAddToCartAmount && isShowExpectedAddToCartAmountOnAtc) {
    return `${addToCartText} · ${formatMoney({ money: expectedAddToCartAmount! })}`;
  }

  return addToCartText;
};

const getAddToCartText = (
  addToCartCtaState: AddToCartCtaState,
  strictLocalize: StrictLocalizeFunction
) => {
  switch (addToCartCtaState) {
    case "UNAVAILABLE":
      return strictLocalize({
        id: "CLmtPM",
        defaultMessage: "Unavailable",
        description: { text: "Not available for purchase" },
      });
    case "UPDATE_DIFFERENT_PRODUCT":
      return strictLocalize({
        id: "qet8kL",
        defaultMessage: "Update cart",
        description: {
          text: "The cart will be updated with new products",
          img: "https://cdn.faire.com/fastly/ede640252a976d4f9dd697768286b5b300ab79c7e50344f7ca1ebd6309b8133d.png",
        },
      });
    case "UPDATE_DEFAULT":
      return strictLocalize({
        id: "oMMkhN",
        defaultMessage: "In cart",
        description: {
          text: "The product already exists in your cart",
          img: "https://cdn.faire.com/fastly/78bfcb6759b477f620bfb8d0f2d17aa7b8d52ee8ab167c20f1fe01b85813b5d9.png",
        },
      });
    case "PREORDER_IN_PROGRESS":
      return strictLocalize({
        id: "ewyuFN",
        defaultMessage: "Added to cart",
        description: {
          text: "The product(s) have been added to your cart",
          img: "https://cdn.faire.com/fastly/8885d0f613ed64b0d5afae2c034023a977ca8db9ca61f71914ca44815516dbd8.png",
        },
      });
    case "PREORDER_DEFAULT":
      return strictLocalize({
        id: "9flwjg",
        defaultMessage: "Preorder",
        description: {
          text: "Will preorder the selected product",
          img: "https://cdn.faire.com/fastly/10c8594d220c55a57268f316b5f30ced1032616bbb0d1c313284cd65628e20d6.png",
        },
      });
    case "DEFAULT_IN_PROGRESS":
      return strictLocalize({
        id: "Vj/6BU",
        defaultMessage: "Added to cart!",
        description: {
          text: "The product(s) have been added to your cart",
          img: "https://cdn.faire.com/fastly/984385cd83fd21ce257b76fd00fab2a975aa674c565b1fee1dc08d3e69e85aff.png",
        },
      });
    case "DEFAULT":
      return strictLocalize({
        id: "uAZvca",
        defaultMessage: "Add to cart",
        description: {
          text: "Prompt to add new product(s) to your cart",
          img: "https://cdn.faire.com/fastly/600cee2f244e58ca7b730bbe0212384e3b18973af0efa234955e3030f9d6cdc3.png",
        },
      });
  }
};
